import { useEffect, useState } from 'react';
import {
  makeStyles,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import AddIcon from '@material-ui/icons/Add';
import HistoryIcon from '@material-ui/icons/History';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import ErrorIcon from '@material-ui/icons/Error';
import { useGenaiBuilderApi, Agent, Document, IngestionJob } from './useGenaiBuilderApi';
import { useIsAdmin } from './useIsAdmin';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  title: {
    flex: 1,
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  statusChip: {
    minWidth: 90,
  },
  error: {
    color: theme.palette.error.main,
    padding: theme.spacing(1),
  },
  jobHistory: {
    minWidth: 400,
  },
}));

function statusIcon(status: IngestionJob['status']) {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon style={{ color: '#4caf50', fontSize: 16 }} />;
    case 'pending':
    case 'processing':
      return <HourglassEmptyIcon style={{ color: '#ff9800', fontSize: 16 }} />;
    case 'failed':
      return <ErrorIcon style={{ color: '#f44336', fontSize: 16 }} />;
  }
}

interface AgentDocumentsProps {
  agent: Agent;
  onBack: () => void;
}

export function AgentDocuments({ agent, onBack }: AgentDocumentsProps) {
  const classes = useStyles();
  const api = useGenaiBuilderApi();
  const { isAdmin } = useIsAdmin();

  const [agentDocs, setAgentDocs] = useState<Document[]>([]);
  const [allDocs, setAllDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [jobsDoc, setJobsDoc] = useState<Document | null>(null);
  const [jobs, setJobs] = useState<IngestionJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const fetchDocs = async () => {
    try {
      setLoading(true);
      setError(null);
      const [agDocs, allDocsList] = await Promise.all([
        api.listAgentDocuments(agent.id),
        api.listDocuments(),
      ]);
      setAgentDocs(agDocs);
      setAllDocs(allDocsList);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [agent.id]);

  const handleAdd = async () => {
    setAdding(true);
    try {
      await Promise.all(
        [...selected].map((docId) => api.associateDocument(agent.id, docId))
      );
      setShowAddDialog(false);
      setSelected(new Set());
      fetchDocs();
    } catch (err: any) {
      setError(err.message ?? 'Failed to add documents');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (docId: string) => {
    setRemovingId(docId);
    try {
      await api.removeDocumentAssociation(agent.id, docId);
      fetchDocs();
    } catch (err: any) {
      setError(err.message ?? 'Failed to remove document');
    } finally {
      setRemovingId(null);
    }
  };

  const handleViewJobs = async (doc: Document) => {
    setJobsDoc(doc);
    setLoadingJobs(true);
    try {
      setJobs(await api.getIngestionJobs(doc.id));
    } catch {
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const agentDocIds = new Set(agentDocs.map((d) => d.id));
  const availableDocs = allDocs.filter((d) => !agentDocIds.has(d.id));

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" className={classes.title}>
          Documents — {agent.name}
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
            disabled={availableDocs.length === 0}
          >
            Add Documents
          </Button>
        )}
      </Box>

      {error && (
        <Typography className={classes.error}>{error}</Typography>
      )}

      {loading && <CircularProgress size={28} />}

      {!loading && agentDocs.length === 0 && (
        <Box className={classes.empty}>
          <Typography>
            No individual documents associated.
          </Typography>
          <Typography variant="caption" style={{ marginTop: 8, display: 'block' }}>
            If RAG Knowledge Scope (in agent settings) has folders selected, the agent
            will search ALL documents in those folders instead. Otherwise, add documents
            here to enable semantic search.
          </Typography>
        </Box>
      )}

      {!loading && agentDocs.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Ingestion</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agentDocs.map((doc) => (
              <TableRow key={doc.id} hover>
                <TableCell>{doc.name}</TableCell>
                <TableCell>
                  <Chip
                    label={doc.file_type.toUpperCase()}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(doc.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Tooltip title="View ingestion history">
                    <IconButton
                      size="small"
                      onClick={() => handleViewJobs(doc)}
                    >
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  {isAdmin && (
                    <Tooltip title="Remove from agent">
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(doc.id)}
                        disabled={removingId === doc.id}
                      >
                        {removingId === doc.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <LinkOffIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Add documents dialog */}
      <Dialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Documents to {agent.name}</DialogTitle>
        <DialogContent>
          {availableDocs.length === 0 ? (
            <Typography color="textSecondary">
              All library documents are already associated with this agent.
            </Typography>
          ) : (
            <List>
              {availableDocs.map((doc) => (
                <ListItem
                  key={doc.id}
                  button
                  onClick={() => {
                    const next = new Set(selected);
                    if (next.has(doc.id)) next.delete(doc.id);
                    else next.add(doc.id);
                    setSelected(next);
                  }}
                >
                  <Checkbox
                    checked={selected.has(doc.id)}
                    disableRipple
                    size="small"
                  />
                  <ListItemText
                    primary={doc.name}
                    secondary={doc.file_type.toUpperCase()}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)} disabled={adding}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            color="primary"
            variant="contained"
            disabled={adding || selected.size === 0}
          >
            {adding ? <CircularProgress size={20} /> : `Add ${selected.size || ''} Document${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ingestion job history dialog */}
      <Dialog
        open={Boolean(jobsDoc)}
        onClose={() => setJobsDoc(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Ingestion History — {jobsDoc?.name}
        </DialogTitle>
        <DialogContent className={classes.jobHistory}>
          {loadingJobs && <CircularProgress size={24} />}
          {!loadingJobs && jobs.length === 0 && (
            <Typography color="textSecondary">No jobs found.</Typography>
          )}
          {!loadingJobs && jobs.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Chunks</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gridGap={4}>
                        {statusIcon(job.status)}
                        <span style={{ textTransform: 'capitalize' }}>
                          {job.status}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell>{job.chunks_created}</TableCell>
                    <TableCell>
                      {job.started_at
                        ? new Date(job.started_at).toLocaleTimeString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {job.completed_at
                        ? new Date(job.completed_at).toLocaleTimeString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {job.error_message ? (
                        <Tooltip title={job.error_message}>
                          <ErrorIcon
                            style={{ color: '#f44336', fontSize: 16 }}
                          />
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobsDoc(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
